import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Download,
  Shield,
  FileText,
  Check,
  Target,
  Award,
  DollarSign,
  BarChart3,
  Building2,
  Globe,
  FlaskConical,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Tabs, TabsContent } from './ui/tabs';
import { ImageWithFallback } from './ui/ImageWithFallback';
import { SimpleAccordion } from './SimpleAccordion';
import { formatCurrencyCompact } from './PropertyDetails/PropertyDetail.utils';
import { DetailPageHeader } from './ui/DetailPageHeader';
import { DetailTabList } from './ui/DetailTabList';
import { StatBox, StatGrid } from './ui/StatBox';
import { MobileCTA } from './ui/MobileCTA';
import { RiskDisclosure } from './ui/RiskDisclosure';
import { useKycGate } from './KycGate';
import { useUser } from '../../contexts/UserContext';
import { offeringsService, Offering } from '../../services/offerings';
import api from '../../services/api';
import { PageRenderer } from './PageBuilder';
import type { PageConfig, EntityContext } from './PageBuilder/types';
import { DocumentAccessNotice } from './DocumentAccessNotice';
import { getProtectedDocumentNotice, openProtectedDocument, type DocumentAccessNotice as DocumentAccessNoticeType } from '../../utils/protectedDocuments';

/* ------------------------------------------------------------------ */
/*  Types for page-content stored in sections_json.pageContent         */
/* ------------------------------------------------------------------ */

interface SummaryItem {
  label: string;
  value: string;
}

interface HighlightItem {
  text: string;
}

interface StrategyBlock {
  title: string;
  content: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface RiskItem {
  text: string;
}

/** Shape of sections_json.pageContent — everything the admin can customise */
export interface OfferingPageContent {
  heroDescription?: string;
  heroImageUrl?: string;

  summaryItems?: SummaryItem[];
  highlights?: HighlightItem[];

  overviewHtml?: string;

  strategyBlocks?: StrategyBlock[];
  strategyIntro?: string;

  faqs?: FAQItem[];

  riskTitle?: string;
  riskDescription?: string;
  risks?: RiskItem[];

  investButtonLabel?: string;
  investButtonUrl?: string;

  /** Tabs to show — defaults to overview,strategy,documents */
  tabs?: string[];
  /** Custom display labels for tabs — e.g. { overview: 'About', strategy: 'Investment Thesis' } */
  tabLabels?: Record<string, string>;
}

/* ------------------------------------------------------------------ */
/*  Deal document type from the admin documents API                    */
/* ------------------------------------------------------------------ */
interface DealDocument {
  id: string;
  deal_id: string;
  kind: string;
  title: string;
  filename: string;
  file_size_bytes?: number;
  mime_type?: string;
  download_url: string;
  created_at: string;
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface OfferingDetailProps {
  onClose: () => void;
  offeringId: string;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  target: <Target className="w-5 h-5 text-[#242424]" />,
  award: <Award className="w-5 h-5 text-[#242424]" />,
  shield: <Shield className="w-5 h-5 text-[#242424]" />,
  trending: <TrendingUp className="w-5 h-5 text-[#242424]" />,
  dollar: <DollarSign className="w-5 h-5 text-[#242424]" />,
  chart: <BarChart3 className="w-5 h-5 text-[#242424]" />,
  building: <Building2 className="w-5 h-5 text-[#242424]" />,
  globe: <Globe className="w-5 h-5 text-[#242424]" />,
};

const fmtBytes = (b?: number) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function OfferingDetail({ onClose, offeringId }: OfferingDetailProps) {
  const navigate = useNavigate();
  const { requireKyc, KycGateModal } = useKycGate();
  const { user } = useUser();

  // Sandbox mode awareness for admin overlay
  const [sandboxPayments, setSandboxPayments] = useState(() =>
    localStorage.getItem('sandboxPayments') === 'true'
  );
  const isAdmin = user?.role === 'admin';
  const toggleSandbox = () => {
    const next = !sandboxPayments;
    setSandboxPayments(next);
    localStorage.setItem('sandboxPayments', String(next));
  };

  const [offering, setOffering] = useState<Offering | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Documents fetched separately
  const [docs, setDocs] = useState<DealDocument[]>([]);
  const [documentNotice, setDocumentNotice] = useState<DocumentAccessNoticeType | null>(null);

  // React component page config (from page_instances linked to this offering)
  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);

  // Lock body scroll
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Fetch offering
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await offeringsService.getOffering(offeringId);
        if (!data) throw new Error('Offering not found');
        setOffering(data);

        // Load documents (investor-visibility filtered: public / investors / holders)
        try {
          const res: any = await api.get(`/offerings/${offeringId}/documents`);
          setDocs(res?.data?.data || res?.data || []);
        } catch { /* no docs */ }

        // Load React page config (if linked)
        try {
          const configRes: any = await api.get(`/offerings/${offeringId}/page-config`);
          const cfg = configRes?.data || configRes;
          if (cfg?.blocks?.length) setPageConfig(cfg);
        } catch { /* no component page */ }
      } catch (err: any) {
        console.error('[OfferingDetail] load failed:', err);
        setError(err?.message || 'Failed to load offering');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [offeringId]);

  /* ---- Derived data from offering ---- */
  const sections = (offering?.sections ?? {}) as Record<string, unknown>;
  const pageContent = (sections.pageContent ?? {}) as OfferingPageContent;
  const kpis = offering?.kpis ?? {};
  const branding = offering?.branding ?? {};

  const title = offering?.title ?? 'Offering';
  const heroImage = pageContent.heroImageUrl || (branding.imageUrl as string) || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&auto=format&fit=crop&q=80';
  const heroDescription = pageContent.heroDescription || offering?.summary || '';

  const summaryItems: SummaryItem[] = pageContent.summaryItems?.length
    ? pageContent.summaryItems
    : buildDefaultSummary(offering);

  const highlights = pageContent.highlights || [];

  const strategyIntro = pageContent.strategyIntro || '';
  const strategyBlocks = pageContent.strategyBlocks || [];

  const faqs = pageContent.faqs || [];

  const risks = pageContent.risks || [];
  const riskTitle = pageContent.riskTitle || 'Important Risk Disclosures';
  const riskDescription = pageContent.riskDescription || 'All investments involve risk. Please review the offering documents carefully before investing.';

  const investLabel = pageContent.investButtonLabel || 'Invest Now';
  const investEnabled = Boolean(offering?.settings?.investEnabled);

  const handleDocumentOpen = async (doc: DealDocument) => {
    try {
      const fullUrl = doc.download_url.startsWith('http') ? doc.download_url : `${window.location.origin}${doc.download_url}`;
      await openProtectedDocument(fullUrl, doc.filename || doc.title);
      setDocumentNotice(null);
    } catch (error) {
      setDocumentNotice(getProtectedDocumentNotice(error));
    }
  };

  const enabledTabs = pageContent.tabs?.length
    ? pageContent.tabs
    : ['overview', 'strategy', 'documents'];

  const customLabels = pageContent.tabLabels || {};
  const tabDefs = [
    { value: 'overview', label: customLabels['overview'] || 'Overview' },
    { value: 'strategy', label: customLabels['strategy'] || 'Strategy' },
    { value: 'documents', label: customLabels['documents'] || 'Documents' },
  ].filter(t => enabledTabs.includes(t.value));



  const handleInvest = () => {
    requireKyc(() => {
      if (pageContent.investButtonUrl) {
        onClose();
        navigate(pageContent.investButtonUrl);
      } else if (offering?.slug) {
        onClose();
        navigate(`/portal/invest/offering/${offering.slug}`);
      }
    });
  };

  /* ---- Render ---- */
  if (loading) {
    return (
      <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !offering) {
    return (
      <div className="fixed inset-0 bg-background z-50 flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">{error || 'Offering not found'}</p>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </div>
    );
  }

  // Compute progress
  const raised = offering.raised || 0;
  const target = offering.raiseTarget || 0;
  const pctFunded = target > 0 ? Math.min(Math.round((raised / target) * 100), 100) : 0;

  // When a published Page Builder page is linked, it fully replaces the legacy layout
  if (pageConfig) {
    return (
      <div className="fixed inset-0 bg-background z-50 overflow-y-auto">
        <DetailPageHeader onClose={onClose} title={title} />
        {isAdmin && (
          <div className={`px-4 py-2 flex items-center justify-between text-sm ${
            sandboxPayments ? 'bg-amber-50 border-b border-amber-200 text-amber-800' : 'bg-muted/50 border-b border-border text-muted-foreground'
          }`}>
            <div className="flex items-center gap-2">
              <FlaskConical className="w-3.5 h-3.5" />
              {sandboxPayments
                ? <span><strong>Sandbox ON</strong> — KYC &amp; accreditation bypassed</span>
                : <span>Sandbox mode off — enable to test invest flows</span>}
            </div>
            <button onClick={toggleSandbox} className="underline text-xs font-medium">
              {sandboxPayments ? 'Disable' : 'Enable Sandbox'}
            </button>
          </div>
        )}
        <PageRenderer
          config={pageConfig}
          entity={{ entityType: 'offering', entityId: offeringId, entityData: offering as any }}
        />
        <KycGateModal />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background z-50 overflow-y-auto">
      <DetailPageHeader onClose={onClose} title={title} />

      {isAdmin && (
        <div className={`px-4 py-2 flex items-center justify-between text-sm ${
          sandboxPayments ? 'bg-amber-50 border-b border-amber-200 text-amber-800' : 'bg-muted/50 border-b border-border text-muted-foreground'
        }`}>
          <div className="flex items-center gap-2">
            <FlaskConical className="w-3.5 h-3.5" />
            {sandboxPayments
              ? <span><strong>Sandbox ON</strong> — KYC &amp; accreditation bypassed</span>
              : <span>Sandbox mode off — enable to test invest flows</span>}
          </div>
          <button onClick={toggleSandbox} className="underline text-xs font-medium">
            {sandboxPayments ? 'Disable' : 'Enable Sandbox'}
          </button>
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6 pb-24 md:pb-6 max-w-7xl">
        {/* Hero Section */}
        <div className="relative h-[280px] sm:h-[400px] lg:h-[500px] bg-muted rounded-none md:rounded-xl overflow-hidden mb-0 md:mb-8 md:mt-6">
          <ImageWithFallback
            src={heroImage}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 lg:p-12">
            <div className="max-w-4xl">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-3">
                {title}
              </h1>
              <p className="text-white/90 text-base md:text-lg max-w-2xl">
                {heroDescription}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mt-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue={tabDefs[0]?.value || 'overview'} className="w-full">
              <DetailTabList tabs={tabDefs} />

              {/* Overview Tab */}
              {enabledTabs.includes('overview') && (
                <TabsContent value="overview" className="mt-6 space-y-6">
                  {pageContent.overviewHtml ? (
                    <Card className="p-6 border-border">
                      <h3 className="text-lg font-display font-semibold text-[#242424] mb-4">About This Offering</h3>
                      <div className="space-y-4">
                        {pageContent.overviewHtml.split('\n').filter(Boolean).map((paragraph, i) => (
                          <p key={i} className="text-sm text-muted-foreground leading-relaxed">{paragraph}</p>
                        ))}
                      </div>
                    </Card>
                  ) : (
                    <Card className="p-6 border-border">
                      <h3 className="text-lg font-display font-semibold text-[#242424] mb-4">About This Offering</h3>
                      <p className="text-sm text-muted-foreground">
                        {offering.summary || 'No overview content has been added yet.'}
                      </p>
                    </Card>
                  )}

                  {/* Key metrics from KPIs */}
                  {Object.keys(kpis).length > 0 && (
                    <Card className="p-6 border-border">
                      <h3 className="text-lg font-display font-semibold text-[#242424] mb-4">Key Metrics</h3>
                      <StatGrid columns={3}>
                        {Object.entries(kpis).map(([key, value]) => (
                          <StatBox key={key} label={formatKpiLabel(key)} value={formatKpiValue(value)} />
                        ))}
                      </StatGrid>
                    </Card>
                  )}
                </TabsContent>
              )}

              {/* Strategy Tab */}
              {enabledTabs.includes('strategy') && (
                <TabsContent value="strategy" className="mt-6 space-y-6">
                  {strategyIntro && (
                    <Card className="p-6 border-border">
                      <h3 className="text-lg font-display font-semibold text-[#242424] mb-4">Investment Strategy</h3>
                      <div className="space-y-4">
                        {strategyIntro.split('\n').filter(Boolean).map((paragraph, i) => (
                          <p key={i} className="text-sm text-muted-foreground leading-relaxed">{paragraph}</p>
                        ))}
                      </div>
                    </Card>
                  )}

                  {strategyBlocks.length > 0 && (
                    <Card className="p-6 border-border">
                      <div className="space-y-4">
                        {strategyBlocks.map((block, idx) => (
                          <div key={idx} className="flex gap-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#F0F0F0] border border-[#E0E0E0] flex items-center justify-center">
                              {ICON_MAP[(['target', 'award', 'shield', 'trending'][idx % 4])] || <Target className="w-5 h-5 text-[#242424]" />}
                            </div>
                            <div>
                              <h4 className="font-semibold text-[#242424] mb-2">{block.title}</h4>
                              <p className="text-sm text-muted-foreground">{block.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {!strategyIntro && strategyBlocks.length === 0 && (
                    <Card className="p-6 border-border">
                      <h3 className="text-lg font-display font-semibold text-[#242424] mb-4">Investment Strategy</h3>
                      <p className="text-sm text-muted-foreground">
                        No strategy content has been added yet.
                      </p>
                    </Card>
                  )}
                </TabsContent>
              )}

              {/* Documents Tab */}
              {enabledTabs.includes('documents') && (
                <TabsContent value="documents" className="mt-6 space-y-6">
                  <Card className="p-6 border-border">
                    <h3 className="text-lg font-display font-semibold text-[#242424] mb-4">Offering Documents</h3>
                    {docs.length > 0 ? (
                      <div className="space-y-3">
                        {documentNotice && <DocumentAccessNotice notice={documentNotice} onDismiss={() => setDocumentNotice(null)} />}
                        {docs.map((doc) => (
                          <button
                            key={doc.id}
                            type="button"
                            onClick={() => handleDocumentOpen(doc)}
                            className="flex w-full items-center justify-between p-4 rounded-lg bg-[#F0F0F0] hover:bg-[#E0E0E0] transition-colors cursor-pointer group border border-border text-left"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#F0F0F0] border border-[#E0E0E0] flex items-center justify-center">
                                <FileText className="w-5 h-5 text-[#242424]" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{doc.title || doc.filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  {fmtBytes(doc.file_size_bytes)}
                                  {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                                </p>
                              </div>
                            </div>
                            <Download className="w-5 h-5 text-muted-foreground group-hover:text-[#242424] transition-colors" />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No documents have been uploaded for this offering yet.
                      </p>
                    )}
                  </Card>
                </TabsContent>
              )}

            </Tabs>

            {/* FAQs */}
            {faqs.length > 0 && (
              <Card className="p-6 border-border">
                <h2 className="text-xl md:text-2xl font-display font-semibold text-[#242424] mb-6">Frequently Asked Questions</h2>
                <SimpleAccordion
                  items={faqs.map((faq, i) => ({
                    value: `faq-${i}`,
                    trigger: faq.question,
                    content: faq.answer,
                  }))}
                />
              </Card>
            )}

            {/* Risk Disclosure */}
            {risks.length > 0 && (
              <RiskDisclosure
                title={riskTitle}
                description={<p>{riskDescription}</p>}
                expandableContent={
                  <div className="space-y-2 pt-4 border-t border-border text-sm text-muted-foreground">
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      {risks.map((r, i) => <li key={i}>{r.text}</li>)}
                    </ul>
                  </div>
                }
                expandLabel="Read Full Risk Disclosure"
              />
            )}
          </div>

          {/* Right Column - Investment Summary Card */}
          <div className="lg:col-span-1">
            <Card className="p-6 border-border sticky top-20">
              <h3 className="text-lg font-display font-semibold text-[#242424] mb-4">Investment Summary</h3>

              <div className="space-y-4 mb-6">
                {summaryItems.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-b-0">
                    <span className="text-sm text-muted-foreground">{item.label}</span>
                    <span className="font-semibold text-[#242424]">{item.value}</span>
                  </div>
                ))}
              </div>

              {/* Fundraising Progress (only if target exists) */}
              {target > 0 && (
                <div className="mb-6 p-4 rounded-lg bg-[#F0F0F0] border border-border">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-medium">
                      {formatCurrencyCompact(raised)}
                    </span>
                    <span className="text-sm text-muted-foreground">raised</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    of {formatCurrencyCompact(target)} target
                  </p>
                  <div className="h-2 bg-[#E0E0E0] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${pctFunded}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-muted-foreground">Progress</span>
                    <span className="text-xs font-medium">{pctFunded}%</span>
                  </div>
                </div>
              )}

              {/* Highlights */}
              {highlights.length > 0 && (
                <div className="space-y-3 mb-6">
                  {highlights.map((h, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#242424] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">{h.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {investEnabled && (
                <div className="space-y-3">
                  <Button className="w-full" size="lg" onClick={handleInvest}>
                    {investLabel}
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {investEnabled && <MobileCTA onClick={handleInvest} label={investLabel} />}
      <KycGateModal />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function buildDefaultSummary(offering: Offering | null): SummaryItem[] {
  if (!offering) return [];
  const items: SummaryItem[] = [];

  const kpis = offering.kpis || {};

  if (offering.minInvest) {
    items.push({ label: 'Minimum Investment', value: `$${offering.minInvest.toLocaleString()}` });
  }
  if (offering.raiseTarget) {
    items.push({ label: 'Target Raise', value: `$${offering.raiseTarget.toLocaleString()}` });
  }
  if (kpis.target_return) {
    items.push({ label: 'Target Return', value: `${kpis.target_return}%` });
  }
  if (kpis.term_length) {
    items.push({ label: 'Term', value: String(kpis.term_length) });
  }
  if (kpis.distribution_frequency) {
    items.push({ label: 'Distributions', value: String(kpis.distribution_frequency) });
  }

  // Always show type
  items.push({ label: 'Offering Type', value: capitalize(offering.type) });

  return items;
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatKpiLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatKpiValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'number') {
    return value >= 1000 ? value.toLocaleString() : String(value);
  }
  return String(value);
}
